'use client';

import { Icon } from '@components/ui';

import styles from './FilterTagsRow.module.scss';
import { FilterTagGroup } from './useFilterTags';

interface FilterAction {
  label: string;
  onClick: () => void;
}

export interface FilterTagsRowActions {
  reset?: FilterAction;
  backToDefault?: FilterAction;
  save?: FilterAction;
}

interface FilterTagsRowProps {
  groups: FilterTagGroup[];
  onRemoveValue: (filterId: string, value: string) => void;
  onRemoveGroup: (filterId: string) => void;
  onTagClick?: (filterId: string) => void;
  actions?: FilterTagsRowActions;
}

const MAX_INLINE = 3;

export const FilterTagsRow = ({ groups, onRemoveValue, onRemoveGroup, onTagClick, actions }: FilterTagsRowProps) => {
  const hasActions = Boolean(actions?.reset || actions?.backToDefault || actions?.save);
  if (groups.length === 0 && !hasActions) return null;

  return (
    <div className={styles.tagsRow}>
      {groups.map(group => {
        const shown = group.labels.slice(0, MAX_INLINE);
        const overflow = group.labels.length - MAX_INLINE;
        return (
          <span
            key={group.filterId}
            className={styles.tagGroup}
          >
            <span className={styles.tagGroupTitle}>{group.title}:</span>
            {shown.map((label, i) => (
              <span
                key={group.values[i]}
                className={styles.tagValue}
              >
                <span
                  className={onTagClick ? `${styles.tagValueText} ${styles.tagValueClickable}` : styles.tagValueText}
                  title={label}
                  onClick={() => onTagClick?.(group.filterId)}
                >
                  {label}
                </span>
                <button
                  className={styles.tagRemove}
                  type="button"
                  onClick={() => onRemoveValue(group.filterId, group.values[i])}
                >
                  <Icon name="xicon" />
                </button>
              </span>
            ))}
            {overflow > 0 && (
              <span className={styles.tagValue}>
                <span className={styles.tagValueText}>+{overflow} more</span>
                <button
                  className={styles.tagRemove}
                  type="button"
                  onClick={() => onRemoveGroup(group.filterId)}
                >
                  <Icon name="xicon" />
                </button>
              </span>
            )}
          </span>
        );
      })}
      {hasActions && (
        <div className={styles.actions}>
          {actions.reset && (
            <button
              type="button"
              className={styles.actionNeutral}
              onClick={actions.reset.onClick}
            >
              {actions.reset.label}
            </button>
          )}
          {actions.backToDefault && (
            <button
              type="button"
              className={styles.actionNeutral}
              onClick={actions.backToDefault.onClick}
            >
              {actions.backToDefault.label}
            </button>
          )}
          {actions.save && (
            <button
              type="button"
              className={styles.actionAccent}
              onClick={actions.save.onClick}
            >
              {actions.save.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
